<template lang="pug">
    input(
        ref="_in",
        type="hidden",
        :id="id",
        :name="name")
</template>

<script>
    /**
     * A form control which has a value and an optional name. If enclosed in an `ib-form` and
     * given a `name`, the entry is represented as a property of the form's `value`.
     */
    export default {
        name: 'ib-form-entry',

        props: {
            /**
             * DOM `id` attribute of input element.
             */
            id: String,
            /**
             * DOM `name` attribute of input element; used as property in enclosing `ib-form`.
             */
            name: String,
            /**
             * Value of field; typically implemented using `v-model`.
             */
            value: {
                type: [String, Number, Boolean, Array, Object],
                default: undefined
            },
            /**
             * Position in sequential keyboard navigation.
             */
            tabindex: [String, Number],
            /**
             * If `true`, form submission will be allowed only if this control has a value.
             */
            required: Boolean,
            /**
             * If `true`, display error state immediately, even if a submit has not been attempted
             */
            immediate: Boolean,
            /**
             * If `true`, this control will appear normal but will not allow user input.
             */
            readonly: Boolean,
            /**
             * If `true`, this control will appear greyed out and will not allow user input.
             */
            disabled: Boolean,
            /**
             * If `true`, this control will appear normal but will not respond to any keyboard or mouse events at all.
             */
            inert: Boolean,
            /**
             * Horizontal alignment: 'start', 'center', or 'end'; default is 'start'.
             */
            align: String,
            /**
             * Width in any CSS units.
             */
            width: String,
            /**
             * Height in any CSS units.
             */
            height: String,
            /**
             * Force an error string
             */
            error: String,
            /**
             * Custom error string for missing value
             */
            errorMissing: String,
            /**
             * Custom error string for invalid value
             */
            errorInvalid: String
        },

        data() {
            return {
                focused: false,
                dispatching: false,
                ivalue: this.value,
                valid: true,
                showvalid: !!this.immediate,
                form: null
            }
        },

        computed: {
            /** Event handlers that apply to all <input> elements */
            __on() {
                const inert = this.inert || this.disabled;
                return {
                    touchstart: inert ? this.__prevent : this.__touchstart,
                    mousedown: inert ? this.__prevent : this.__mousedown,
                    mouseup: inert ? this.__prevent : this.__mouseup,
                    click: inert ? this.__prevent : this.__click,
                    focus: inert ? this.__preventfocus : this.__focus,
                    blur: this.__blur
                };
            },

            /** Event handlers that apply to shim elements */
            __fwd() {
                const inert = this.inert || this.disabled;
                return {
                    mousedown: inert ? this.__prevent : this.__fwdmousedown,
                    click: inert ? this.__prevent : this.__fwdclick
                };
            }
        },

        watch: {
            error(to) {
                this.validate();
            },

            valid(to, from) {
                if (!to && from) {
                    this.$emit('invalid');
                }
            },

            value(value) {
                if (!Object.is(this.ivalue, value)) {
                    this.ivalue = value;
                    this.showvalid = !!this.immediate;
                    this.$nextTick(this._sync);
                }
            }
        },

        mounted() {
            let cmp = this;
            while ((cmp = cmp.$parent)) {
                if (cmp.isForm) {
                    this.form = cmp;
                    this.form._add(this);
                    break;
                }
            }
            this.$nextTick(this._sync);
        },

        beforeDestroy() {
            if (this.form) {
                this.form._remove(this);
            }
        },

        methods: {
            /**
             * Remove keyboard focus from this control.
             */
            blur() {
                const input = this.$refs.proxy || this.$refs._in;
                if (input) {
                    input.blur();
                }
            },

            /**
             * Set keyboard focus to this control.
             */
            focus() {
                if (!this.disabled) {
                    const input = this.$refs.proxy || this.$refs._in;
                    if (input) {
                        input.focus();
                    }
                }
            },

            /**
             * Validation: default implementation uses HTML5 form validation.
             */
            validate() {
                this.valid = !this.error && this.$refs._in.checkValidity();
                this.__refresherror();
                return this.valid;
            },

            getvalidity() {
                const input = this.$refs._in;
                return this.error ||
                    (input.validity.valueMissing && this.errorMissing) ||
                    (input.validity.patternMismatch && this.errorInvalid) ||
                    input.validationMessage;
            },

            /**
             * Reset to initial value (if `v-model` is not used) and reset validity state.
             */
            reset() {
                this.showvalid = !!this.immediate;
                this.ivalue = this.value;
                this.$nextTick(this._sync);
            },

            /** @private */
            _emit(value) {
                this.ivalue = value;
                this.$emit('input', value);
            },

            /** @private */
            _set(value) {
                if (!this.inert && !this.disabled && !this.readonly) {
                    this.ivalue = value;
                    this.$emit('input', value);
                    this.$nextTick(this._sync);
                }
            },

            /** @private */
            _sync() {
            },

            /** @private */
            __fwdmousedown(e) {
                if (!['INPUT','SELECT','TEXTAREA','BUTTON'].includes(e.target.tagName)) {
                    this.focus();
                    this.__mousedown(e);
                    e.preventDefault();
                }
            },

            /** @private */
            __fwdclick(e) {
                const input = this.$refs._in;
                if (input) {
                    input.click();
                }
            },

            /** @private */
            __prevent(e) {
                e.preventDefault();
            },

            /** @private */
            __preventfocus(e) {
                //FIXME: result of [autofocus] behavior
                if (this.$el.tabIndex >= 0) {
                    this.$el.focus();
                } else {
                    e.target.blur();
                }
                e.preventDefault();
                e.stopImmediatePropagation();
            },

            /** @private */
            __touchstart(e) {
                if (this._touchstart) {
                    this._touchstart(e);
                }
                this.$emit('touchstart', e);
            },

            /** @private */
            __mousedown(e) {
                if (this._mousedown) {
                    this._mousedown(e);
                }
                this.$emit('mousedown', e);
            },

            /** @private */
            __mouseup(e) {
                if (this._mouseup) {
                    this._mouseup(e);
                }
                this.$emit('mouseup', e);
            },

            /** @private */
            __click(e) {
                if (this._click) {
                    this._click(e);
                }
                this.$emit('click', e);
            },

            /** @private */
            __focus(e) {
                this.$util.scrollIntoView(this.$el);
                this.focused = true;
                this.__refresherror();
                if (this._focus) {
                    this._focus(e);
                }
                this.$emit('focus', e);
            },

            /** @private */
            __blur(e) {
                this.focused = false;
                if (this._blur) {
                    this._blur(e);
                }
                this.$tooltip.close(this.$el, 'error');
                this.$emit('blur', e);
            },

            /** @private */
            __refresherror() {
                if (this.showvalid && !this.valid && (this.focused || this.$refs.proxy)) {
                    this.$tooltip(this.getvalidity(), {
                        anchor: this.$el,
                        position: this.errorPosition,
                        align: this.align || 'start',
                        tone: 'error'
                    });
                } else {
                    this.$tooltip.close(this.$el, 'error');
                }
            }
        }
    }
</script>
